import { useState, useCallback } from 'react';
import { ApolloClient, useMutation, useApolloClient } from '@apollo/client';
import { reportError } from '../../lib/reportError';
import { tc } from '../../lib/trackingContext';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useUser } from '../../hooks/useUser';
import { getCookies } from '../../hooks/useCookies';
import { useCartVersion, useCart } from '../../hooks/useCart';
import { GET_CART_QUERY, Cart, LineItem } from '../../hooks/useCart/cart.rest';
import { GET_SAVED_ITEMS_QUERY } from '../../hooks/useSavedItems/savedItems.rest';
import { useCountry } from '../CountrySelector';
import { useAuthModal } from '../../hooks/useAuthModal';
import {
  DELETE_LINE_ITEM_MUTATION,
  UPDATE_LINE_ITEM_MUTATION,
  UpdateLineItemMutationInputAttributes,
  LineItemType
} from './lineItemMutations.rest';

export enum Mode {
  Resting,
  Deleting,
  Updating,
  Deleted
}

const updateSavedItemsCache = <T>(
  client: ApolloClient<T>,
  countryCode: string,
  update: (items: LineItem[]) => LineItem[]
) => {
  let data;

  try {
    data = client.cache.readQuery({
      query: GET_SAVED_ITEMS_QUERY,
      variables: { countryCode }
    });
  } catch (error) {
    // if the query is not in the cache, do nothing
  }

  if (!data) return;

  client.writeQuery({
    query: GET_SAVED_ITEMS_QUERY,
    variables: {
      countryCode
    },
    data: {
      ...data,
      savedItems: {
        ...data.savedItems,
        data: update(data.savedItems.data)
      }
    }
  });
};

const updateCartCache = <T>(client: ApolloClient<T>, data?: Cart[]) => {
  if (!data) return;

  client.writeQuery({
    query: GET_CART_QUERY,
    variables: {
      cartId: data[0].id,
      countryCode: data[0].attributes.countryCode,
      cartVersion: data[0].attributes.version
    },
    data: {
      userCart: { __typename: 'RestUserCart', data: [data[0]] }
    }
  });
};

const SHOW_DELETED_MS = 2000;

const useDeleteMutation = (item: LineItem, setMode: (mode: Mode) => void) => {
  const client = useApolloClient();
  const { country } = useCountry();
  const { showErrorModal } = useErrorModal();
  const cartVersion = useCartVersion();
  const [deleteLineItem] = useMutation(DELETE_LINE_ITEM_MUTATION, {
    variables: { id: item.id, cartVersion, countryCode: country.alpha2Code },
    // don't update the cache here, we update it manually after showing the 'deleted' message
    fetchPolicy: 'no-cache'
  });

  return useCallback(async () => {
    setMode(Mode.Deleting);
    try {
      const { data } = await deleteLineItem();
      setMode(Mode.Deleted);
      setTimeout(() => {
        updateCartCache(client, data?.deleteLineItem.data);
        updateSavedItemsCache(client, country.alpha2Code, items =>
          items.filter(savedItem => savedItem.id !== item.id)
        );
      }, SHOW_DELETED_MS);
      item.type === 'cart_items'
        ? tc.track('Product Removed', { addToScopeOnce: { lineItem: item } })
        : tc.track('Product Removed from Wishlist', { addToScopeOnce: { lineItem: item } });
    } catch (error) {
      setMode(Mode.Resting);
      showErrorModal();
      reportError(error);
    }
  }, [setMode, deleteLineItem, client, item, country, showErrorModal]);
};

const ITEM_TYPES: { [key: string]: LineItemType } = {
  saved_items: 'SavedItem',
  cart_items: 'CartItem'
};

const useUpdateMutation = (
  { id, type, attributes: { skuId, quantity } }: LineItem,
  setMode: (mode: Mode) => void
) => {
  const client = useApolloClient();
  const { country } = useCountry();
  const { showErrorModal } = useErrorModal();
  const cartVersion = useCartVersion();
  const [updateLineItem] = useMutation(UPDATE_LINE_ITEM_MUTATION);

  return useCallback(
    async (attributes: Partial<UpdateLineItemMutationInputAttributes>) => {
      setMode(Mode.Updating);
      try {
        await updateLineItem({
          variables: {
            id,
            cartVersion,
            input: {
              data: {
                type: 'line_items',
                id,
                attributes: {
                  sku_id: skuId,
                  quantity,
                  type: ITEM_TYPES[type],
                  ...attributes
                }
              }
            },
            countryCode: country.alpha2Code
          }
        });
        if (type === 'saved_items' || attributes.type === 'SavedItem')
          await client.query({
            query: GET_SAVED_ITEMS_QUERY,
            variables: {
              countryCode: country.alpha2Code
            },
            fetchPolicy: 'network-only'
          });
      } catch (error) {
        showErrorModal();
        reportError(error);
      }
      setMode(Mode.Resting);
    },
    [
      cartVersion,
      setMode,
      updateLineItem,
      id,
      client,
      quantity,
      skuId,
      type,
      country,
      showErrorModal
    ]
  );
};

const useSaveForLaterMutation = (item: LineItem, setMode: (mode: Mode) => void) => {
  const client = useApolloClient();
  const { country } = useCountry();
  const { user } = useUser();
  const { openAuthModal, closeAuthModal } = useAuthModal();
  const cartVersion = useCartVersion();
  const handleUpdate = useUpdateMutation(item, setMode);

  const proceedSaveForLater = useCallback(async () => {
    await handleUpdate({ type: 'SavedItem' });
    tc.track('Product Added to Wishlist', { addToScopeOnce: { lineItem: item } });
  }, [handleUpdate, item]);

  const handleAuthSuccess = useCallback(async () => {
    const { cartId } = getCookies();
    if (!cartId) throw new Error('useSaveForLaterMutation: cart not found');
    await client.query({
      query: GET_CART_QUERY,
      variables: { cartId, cartVersion, countryCode: country.alpha2Code }
    });
    await client.query({
      query: GET_SAVED_ITEMS_QUERY,
      variables: { countryCode: country.alpha2Code }
    });
    await proceedSaveForLater();
  }, [proceedSaveForLater, cartVersion, client, country]);

  const handleSaveForLater = useCallback(
    () => (user.isLoggedIn ? proceedSaveForLater() : openAuthModal(handleAuthSuccess)),
    [user.isLoggedIn, proceedSaveForLater, openAuthModal, handleAuthSuccess]
  );

  const handleHideAuth = useCallback(() => closeAuthModal(), [closeAuthModal]);

  return { handleSaveForLater, handleHideAuth };
};

export const useLineItemMutations = (item: LineItem) => {
  const [mode, setMode] = useState(Mode.Resting);
  const handleDelete = useDeleteMutation(item, setMode);
  const handleUpdate = useUpdateMutation(item, setMode);
  const { cart } = useCart();

  const { handleSaveForLater, handleHideAuth } = useSaveForLaterMutation(item, setMode);

  const handleChangeQuantity = useCallback((quantity: number) => handleUpdate({ quantity }), [
    handleUpdate
  ]);

  const handleChangeSize = useCallback((skuId: number) => handleUpdate({ sku_id: skuId }), [
    handleUpdate
  ]);

  const handleMoveToBag = useCallback(async () => {
    await handleUpdate({ type: 'CartItem' });
    tc.track('Product Added', { addToScopeOnce: { lineItem: item, cart } });
    tc.track('Wishlist Product Added to Cart', { addToScopeOnce: { lineItem: item } });
  }, [cart, handleUpdate, item]);

  const handleChangeType = useCallback(
    (type: LineItemType) => (type === 'SavedItem' ? handleSaveForLater() : handleMoveToBag()),
    [handleSaveForLater, handleMoveToBag]
  );

  return {
    mode,
    handleHideAuth,
    handleDelete,
    handleChangeQuantity,
    handleChangeSize,
    handleChangeType
  };
};
